/**
 * Die fünf Abschnitte der Einstellungen (Board „Preisautomatik · Einstellungen“) und welches Feld zu
 * welchem Abschnitt gehört. Daraus entstehen die Marker „geändert“ und „Fehler“ in der
 * Abschnittsleiste – für Eingaben aus dem Formular wie für Befunde des Servers.
 */
import type { PricingConfigurationFormValues } from './pricingConfigurationSchema';

export const SECTION_KEYS = ['status', 'prices', 'minStay', 'schedule', 'safety'] as const;
export type SectionKey = (typeof SECTION_KEYS)[number];

export const SECTION_TITLE: Record<SectionKey, string> = {
  status: 'Status & Strategie',
  prices: 'Preisgrenzen & Regeln',
  minStay: 'Mindestaufenthalt & Lücken',
  schedule: 'Zeitplan & Übertragung',
  safety: 'Sicherheit & Vorschau',
};

/** Kurzform in der Abschnittsleiste und im Freigabeweg. */
export const SECTION_SHORT: Record<SectionKey, string> = {
  status: 'Status & Strategie',
  prices: 'Preisgrenzen',
  minStay: 'Mindestaufenthalt',
  schedule: 'Zeitplan',
  safety: 'Sicherheit',
};

export const sectionNumber = (key: SectionKey) => SECTION_KEYS.indexOf(key) + 1;
export const sectionAnchor = (key: SectionKey) => `pricing-section-${key}`;

type FieldName =
  | keyof PricingConfigurationFormValues
  | 'currency'
  | 'apartmentCurrency'
  | 'horizonDays';

const FIELD_SECTION: Record<FieldName, SectionKey> = {
  mode: 'status',
  strategyProfileVersionId: 'status',
  currency: 'prices',
  minimumPrice: 'prices',
  referencePrice: 'prices',
  maximumPrice: 'prices',
  lastMinuteDiscountEnabled: 'prices',
  gapDiscountEnabled: 'prices',
  regularMinimumStay: 'minStay',
  fridaySaturdayMinimumStay: 'minStay',
  maximumGapLength: 'minStay',
  gapPolicy: 'minStay',
  gapWithinDays: 'minStay',
  minimumStayTiersEnabled: 'minStay',
  minimumStayLeadTimeTiers: 'minStay',
  calculationIntervalHours: 'schedule',
  calculationStartLocalTime: 'schedule',
  publicationWindows: 'schedule',
  horizonDays: 'schedule',
  apartmentCurrency: 'prices',
};

/**
 * Wurzel eines Feldpfads, gleich ob aus dem Formular (`publicationWindows.0.endLocalTime`) oder
 * vom Server (`PublicationWindows[0].EndLocalTime`, PascalCase).
 */
export function fieldRoot(path: string): string {
  const head = path.split(/[.[]/)[0] ?? '';
  return head.charAt(0).toLowerCase() + head.slice(1);
}

/** Abschnitt eines Feldes; ein unbekanntes Feld landet bei „Status & Strategie“. */
export function sectionOfField(path: string): SectionKey {
  return FIELD_SECTION[fieldRoot(path) as FieldName] ?? 'status';
}

export interface FieldIssue {
  /** Wurzelfeld im Formular, z. B. `minimumPrice` oder `publicationWindows`. */
  field: string;
  section: SectionKey;
  message: string;
  source: 'client' | 'server';
  /** Unübersetzter Servertext, wenn kein eigener Satz passt – bleibt klein sichtbar. */
  detail?: string;
}

/** Abschnitte mit geänderten Feldern (`dirtyFields` aus react-hook-form). */
export function changedSections(dirtyFields: object): Set<SectionKey> {
  const sections = new Set<SectionKey>();
  for (const [field, value] of Object.entries(dirtyFields)) {
    if (hasTrue(value)) sections.add(sectionOfField(field));
  }
  return sections;
}

const hasTrue = (value: unknown): boolean =>
  value === true ||
  (typeof value === 'object' && value !== null && Object.values(value).some(hasTrue));

export type SectionMarker = 'error' | 'changed' | null;

/** Ein Fehler überdeckt „geändert“: er ist das, was zuerst zu tun ist. */
export function sectionMarkers(
  changed: Set<SectionKey>,
  issues: readonly FieldIssue[],
): Record<SectionKey, SectionMarker> {
  const failing = new Set(issues.map((issue) => issue.section));
  return Object.fromEntries(
    SECTION_KEYS.map((key) => [
      key,
      failing.has(key) ? 'error' : changed.has(key) ? 'changed' : null,
    ]),
  ) as Record<SectionKey, SectionMarker>;
}
